<template>
    <div id="punch-log-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.punchLog.searchByEmployeeNoOrName')"
                        solo
                        class="subheader-margin"
                        height="40px"
                        @keyup.enter="getPunchLogPagination()">
                    </v-text-field>
                </div>
                <div class="ml-3">
                    <span class="date-picker-label">
                        {{ $vuetify.lang.t('$vuetify.punchLog.startDate')}}
                    </span>
                    <v-menu
                        v-model="startDatePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="startDateFormatted"
                                :label="$vuetify.lang.t('$vuetify.punchLog.startDate')"
                                prepend-inner-icon="event"
                                @click:prepend-inner="startDatePickerShow = true"
                                @click:clear="startDate = null"
                                readonly
                                v-on="on"
                                solo
                                height="40px"
                                background-color="#ffffff">
                            </v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            v-model="startDate"
                            @change="formatStartDate"
                            @input="startDatePickerShow = false">
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="ml-3">
                    <span class="date-picker-label">
                        {{ $vuetify.lang.t('$vuetify.punchLog.endDate')}}
                    </span>
                    <v-menu
                        v-model="endDatePickerShow"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="234px">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                v-model="endDateFormatted"
                                :label="$vuetify.lang.t('$vuetify.punchLog.endDate')"
                                prepend-inner-icon="event"
                                @click:clear="endDate = null"
                                @click:prepend-inner="endDatePickerShow = true"
                                readonly
                                v-on="on"
                                solo
                                height="40px"
                                background-color="#ffffff">
                            </v-text-field>
                        </template>
                        <v-date-picker
                            :locale="localeLang"
                            v-model="endDate"
                            @change="formatEndDate"
                            @input="endDatePickerShow = false">
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="ml-3">
                    <v-btn
                        @click="searchPunchLog()"
                        class="subheader-margin"
                        color="primary"
                        height="40px"
                        outlined>
                        {{ $vuetify.lang.t('$vuetify.punchLog.search')}}
                    </v-btn>
                </div>
            </div>
        </div>
        <!-- END HEADER -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.time`]="{ item }">
                    <span
                        v-if="item.time && item.time != '00:00'">
                        {{ item.time }}
                    </span>
                    <span v-else>
                        -
                    </span>
                </template>

                <template v-slot:[`item.image`]="{ item }">
                    <v-avatar
                        v-if="item.image"
                        class="pointer ml-3"
                        size="19"
                        @click="showImage(item.image)">
                        <img
                            src="@/assets/icons/image.jpg">
                    </v-avatar>
                </template>
            </v-data-table>
        </v-card>

        <div
            v-if="pageCount"
            class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <image-preview
            ref="imagePreview" />
    </div>
</template>

<script>
import ImagePreview from '@/components/ImagePreview'
export default {
    name: 'punchLog',

    components: {
        ImagePreview
    },

    data () {
        return {
            loading: false,
            search: '',
            startDate: this.$util.getCurrentDate(),
            endDate: this.$util.getCurrentDate(),
            startDatePickerShow: false,
            endDatePickerShow: false,
            startDateFormatted: '',
            endDateFormatted: '',
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            branch: null
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },

        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.punchLog.employeeNo'), value: 'employee__employee_no', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.employeeName'), value: 'employee__fullname', width: '30%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.date'), value: 'date', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLog.time'), value: 'time', width: '15%' },
                { text: '', value: 'image', sortable: false, width: '20%' }
            ]
        }
    },

    methods: {
        showImage (path) {
            this.$refs.imagePreview.toggleShow(path)
        },

        async searchPunchLog () {
            this.options.page = 1
            await this.getPunchLogPagination()
        },

        async getPunchLogPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options
            let url = `punch-logs?page=${page}&branch=${this.branch}&itemsPerPage=${itemsPerPage}`
            if (sortBy && sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc && sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.startDate) {
                url += `&startDate=${this.startDate}`
            }

            if (this.endDate) {
                url += `&endDate=${this.endDate}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
            }
            this.loading = false
        },

        async getBranch () {
            this.branch = localStorage.getItem('currentBranchId')
            if (this.branch) {
                await this.getPunchLogPagination()
            }
        },

        formatStartDate () {
            this.startDateFormatted = this.$util.formatDate(this.startDate)
        },

        formatEndDate () {
            this.endDateFormatted = this.$util.formatDate(this.endDate)
        }
    },

    async mounted () {
        await this.getBranch()
        this.formatStartDate()
        this.formatEndDate()
    },

    watch: {
        options: {
            async handler () {
                if (this.branch) {
                    await this.getPunchLogPagination()
                }
            },
            deep: true
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.getPunchLogPagination()
            },
            deep: true
        }
    }

}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
}
::v-deep .search-input .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}
.subheader-margin {
    margin-top: 24px;
}
.date-picker-label {
    font-size: 12px;
    color: #757575;
}
.action-header {
    margin-top: 0.5rem;
}
.pointer {
    cursor: pointer;
}
</style>
